import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Text from "components/atoms/text/Text";

const Container = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: ${(props) => props.margin};
    width: ${(props) => props.width};
    max-width: ${(props) => props.maxWidth};
    height: ${(props) => (props.height ? props.height : "32px")};
    background-color: ${(props) => (props.disabled ? "#F2F6F8" : "var(--color-White)")};
    border: ${(props) => (props.isOpen ? "solid 1px var(--color-Key)" : "solid 1px var(--color-Outline)")};
    border-radius: 4px;
`;

const SelectedItem = styled.div`
    display: flex;
    align-items: center;
    margin: 0 4px 0 0;
    padding: 0 0 0 12px;
    width: calc(100% - 24px);
    max-width: calc(100% - 24px);
    height: 100%;
    word-break: break-all;
`;

const IconBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 10px 0 10px;
    width: 10px;
    height: 10px;
    transform: rotate(${(props) => props.reverse || "0deg"});
`;

const OptionContainer = styled.div`
    position: fixed;
    top: ${(props) => props.top}px;
    left: ${(props) => props.left}px;
    width: ${(props) => props.width}px;
    min-width: ${(props) => props.minWidth};
    visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
    z-index: 1000;
`;

const OptionWrapper = styled.div`
    width: 100%;
    padding: 4px 2px 3px 6px;
    background-color: var(--color-White);
    border-radius: 4px;
    border: solid 1px var(--color-Key);
`;

const OptionList = styled.ul`
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    max-height: ${(props) => props.optionMaxHeight || "120px"};
    border-radius: 2px;
    overflow: auto;

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
        display: initial;
    }

    &::-webkit-scrollbar-thumb {
        min-width: 40px;
        min-height: 20px;
        background-color: var(--color-DarkKey);
    }

    &::-webkit-scrollbar-track {
        background-color: var(--color-ButtonHover4);
    }
`;

const OptionItem = styled.li`
    width: 100%;
    height: 32px;
    list-style: none;
`;

const OptionItemInner = styled.div`
    display: flex;
    align-items: center;
    margin: 1px 4px 1px 0;
    padding: 0 8px;
    width: calc(100% - 4px);
    height: 30px;
    border-radius: 2px;
    border: transparent;
    background-color: var(--color-White);
    transition: all 50ms ease-in;

    &:hover {
        background-color: var(--color-ButtonHover4);
        cursor: pointer;
    }
`;

const OptionItemText = styled(Text)`
    width: 100%;
    font-size: 1.4rem;
    text-align: ${(props) => props.textAlign};

    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const SelectedOptionItemInner = styled(OptionItemInner)`
    background-color: var(--color-Key) !important;

    p {
        color: var(--color-White);
    }
`;

function SelectBox(props) {
    const selectedOptionName = props.items?.find((item) => item.id === props.value)?.name;

    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({
        top: -9999,
        left: -9999,
        width: 0,
    });
    const dropdownRef = useRef(null);
    const optionContainerRef = useRef(null);

    const parseSize = (value, fallback) => {
        if (!value) {
            return fallback;
        }

        if (typeof value === "number") {
            return value;
        }

        if (typeof value === "string") {
            if (value.endsWith("px")) {
                return Number(value.replace("px", ""));
            }

            const parsedValue = Number(value);
            if (!Number.isNaN(parsedValue)) {
                return parsedValue;
            }
        }

        return fallback;
    };

    const getDropdownWidth = (triggerRect) => {
        if (!props.optionWidth || props.optionWidth === "100%") {
            return triggerRect.width;
        }

        if (typeof props.optionWidth === "string" && props.optionWidth.endsWith("%")) {
            return triggerRect.width;
        }

        return parseSize(props.optionWidth, triggerRect.width);
    };

    const updateDropdownPosition = () => {
        if (!dropdownRef.current) {
            return;
        }

        const triggerRect = dropdownRef.current.getBoundingClientRect();
        const dropdownWidth = getDropdownWidth(triggerRect);
        const dropdownHeight = optionContainerRef.current?.offsetHeight || 0;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const defaultOffset = props.height ? parseSize(props.height, triggerRect.height + 4) + 4 : 36;
        const customOffset = props.top ? parseSize(props.top, defaultOffset) : defaultOffset;

        const openAbove = props.topBottom === "bottom";
        let nextTop = openAbove ? triggerRect.bottom - customOffset - dropdownHeight : triggerRect.top + customOffset;

        if (!openAbove && dropdownHeight > 0 && nextTop + dropdownHeight > viewportHeight - 8) {
            nextTop = Math.max(8, triggerRect.top - dropdownHeight - 4);
        }

        if (openAbove && nextTop < 8) {
            nextTop = Math.min(viewportHeight - dropdownHeight - 8, triggerRect.bottom + 4);
        }

        let nextLeft = props.optionAlign === "left" ? triggerRect.left - 2 : triggerRect.right - dropdownWidth;
        nextLeft = Math.max(8, Math.min(nextLeft, viewportWidth - dropdownWidth - 8));

        setDropdownStyle({
            top: nextTop,
            left: nextLeft,
            width: dropdownWidth,
        });
    };

    // 영역 외 영역을 클릭하면 드롭다운 닫힘
    useEffect(() => {
        const handleOutsideClick = (event) => {
            const isTriggerClick = dropdownRef.current?.contains(event.target);
            const isDropdownClick = optionContainerRef.current?.contains(event.target);

            if (!isTriggerClick && !isDropdownClick) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        updateDropdownPosition();
    }, [isOpen, props.items, props.optionAlign, props.optionWidth, props.optionMinWidth, props.topBottom, props.top]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePositionChange = () => {
            updateDropdownPosition();
        };

        window.addEventListener("resize", handlePositionChange);
        document.addEventListener("scroll", handlePositionChange, true);

        return () => {
            window.removeEventListener("resize", handlePositionChange);
            document.removeEventListener("scroll", handlePositionChange, true);
        };
    }, [isOpen, props.items, props.optionAlign, props.optionWidth, props.optionMinWidth, props.topBottom, props.top]);

    return (
        <>
            <Container
                width={props.width}
                height={props.height}
                maxWidth={props.maxWidth}
                margin={props.margin}
                disabled={props.disabled}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(props.disabled ? false : !isOpen);
                }}
                onChange={props.onChange}
                isOpen={isOpen}
                ref={dropdownRef}
            >
                <SelectedItem>
                    <OptionItemText textAlign={props.textAlign || "center"}>{selectedOptionName || ""}</OptionItemText>
                </SelectedItem>
                <IconBox reverse={props.reverse}>
                    <Icon name={isOpen ? "arrowUp" : "arrowDown"} color={"var(--color-SubBlack)"} />
                </IconBox>
            </Container>
            {createPortal(
                <OptionContainer
                    left={dropdownStyle.left}
                    top={dropdownStyle.top}
                    width={dropdownStyle.width}
                    minWidth={props.optionMinWidth}
                    isOpen={isOpen}
                    ref={optionContainerRef}
                >
                    <OptionWrapper>
                        <OptionList optionMaxHeight={props.optionMaxHeight}>
                            {props.items?.map((item, index) => (
                                <OptionItem
                                    key={index}
                                    value={item.id}
                                    onClick={() => {
                                        props.onChange(item.id);
                                        setIsOpen(false);
                                    }}
                                >
                                    {props.value === item.id ? (
                                        <SelectedOptionItemInner>
                                            <OptionItemText textAlign={props.textAlign || "center"}>
                                                {item.name || item}
                                            </OptionItemText>
                                        </SelectedOptionItemInner>
                                    ) : (
                                        <OptionItemInner>
                                            <OptionItemText textAlign={props.textAlign || "center"}>
                                                {item.name || item}
                                            </OptionItemText>
                                        </OptionItemInner>
                                    )}
                                </OptionItem>
                            ))}
                        </OptionList>
                    </OptionWrapper>
                </OptionContainer>,
                document.body,
            )}
        </>
    );
}

export default SelectBox;
